import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IFrame } from '@stomp/stompjs';
import { RxStomp, RxStompConfig } from '@stomp/rx-stomp';
import { Observable, Subscription, timer } from 'rxjs';
import { environment } from '../../environments/environment';
import { AuthService } from '../auth/auth.service';

const STOMP_ERROR_CODE_HEADER = 'x-axxyl-error-code';

@Injectable({ providedIn: 'root' })
export class WebSocketService {
  private rxStomp: RxStomp | null = null;
  private stompErrorSubscription: Subscription | null = null;
  private recoveringExpiredToken = false;
  private recoveringNetwork = false;
  private reconnectStopped = false;
  private recoveryAttempt = 0;
  private recoveryRetrySubscription: Subscription | null = null;
  private connectedSubscription: Subscription | null = null;
  private readonly auth = inject(AuthService);
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    this.auth.sessionEnded$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.reconnectStopped = true;
      this.disconnect();
    });
    this.destroyRef.onDestroy(() => this.disconnect());
  }

  connect(): void {
    if (!environment.websocketUrl || !this.auth.isAuthenticated()) return;
    if (this.rxStomp?.active) return;

    this.reconnectStopped = false;
    if (!this.rxStomp) this.rxStomp = this.createClient();
    this.activateClient(this.rxStomp);
  }

  watch<T>(destination: string): Observable<T> {
    if (!this.rxStomp) this.connect();
    return new Observable<T>((subscriber) => {
      const client = this.rxStomp;
      if (!client) {
        subscriber.error(new Error('WebSocket is unavailable'));
        return;
      }

      const subscription = client.watch(destination).subscribe({
        next: (message) => {
          try {
            subscriber.next(JSON.parse(message.body) as T);
          } catch {
            subscriber.error(new Error('Failed to parse WebSocket message'));
          }
        },
        error: (error) => subscriber.error(error),
      });
      return () => subscription.unsubscribe();
    });
  }

  send(destination: string, body: unknown): void {
    this.rxStomp?.publish({ destination, body: JSON.stringify(body) });
  }

  disconnect(): void {
    const client = this.rxStomp;
    if (!client) return;
    // Detach the client before the asynchronous close callback fires. A user
    // can sign in again while an older socket is still closing; frames from
    // that socket must never terminate or refresh the replacement session.
    this.rxStomp = null;
    this.cancelRecoveryRetry();
    this.stompErrorSubscription?.unsubscribe();
    this.stompErrorSubscription = null;
    this.connectedSubscription?.unsubscribe();
    this.connectedSubscription = null;
    this.recoveringNetwork = false;
    void client.deactivate({ force: true });
  }

  private createClient(): RxStomp {
    const client = new RxStomp();
    const config: RxStompConfig = {
      brokerURL: environment.websocketUrl,
      heartbeatIncoming: 10_000,
      heartbeatOutgoing: 10_000,
      reconnectDelay: 0,
      beforeConnect: (connectingClient) => {
        const token = this.auth.getAccessToken();
        connectingClient.configure({
          connectHeaders: token ? { Authorization: `Bearer ${token}` } : {},
        });
      },
    };
    client.configure(config);

    this.stompErrorSubscription = client.stompErrors$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((frame) => this.handleSessionErrorCode(this.stompCode(frame), client));
    this.connectedSubscription = client.connected$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.recoveryAttempt = 0;
        this.recoveringNetwork = false;
      });

    return client;
  }

  /**
   * RxStomp installs its own close callback inside activate(). Install our
   * wrapper afterwards on every explicit activation so it cannot be replaced.
   */
  private activateClient(client: RxStomp): void {
    client.activate();
    const stompClient = client.stompClient;
    const rxStompClose = stompClient.onWebSocketClose;
    stompClient.onWebSocketClose = (event: CloseEvent) => {
      rxStompClose(event);
      if (event.code === 4401) this.handleSessionErrorCode('SESSION_TOKEN_EXPIRED', client);
      else if (event.code === 4403) this.handleSessionErrorCode('SESSION_REVOKED', client);
      else this.scheduleNetworkReconnect(client);
    };
  }

  private stompCode(frame: IFrame): string | undefined {
    const headerCode = frame.headers[STOMP_ERROR_CODE_HEADER];
    if (headerCode) return headerCode.trim().toUpperCase();
    try {
      const body = JSON.parse(frame.body) as { code?: unknown };
      return typeof body.code === 'string' ? body.code.trim().toUpperCase() : undefined;
    } catch {
      return undefined;
    }
  }

  private handleSessionErrorCode(code?: string, sourceClient = this.rxStomp): void {
    if (!sourceClient || sourceClient !== this.rxStomp) return;

    if (code === 'SESSION_REVOKED') {
      if (this.reconnectStopped) return;
      this.reconnectStopped = true;
      this.cancelRecoveryRetry();
      const client = this.rxStomp;
      if (client) void client.deactivate({ force: true });
      this.auth.forceLogout('revoked');
      return;
    }

    if (code !== 'SESSION_TOKEN_EXPIRED' || this.recoveringExpiredToken || this.reconnectStopped) {
      return;
    }

    this.recoveringExpiredToken = true;
    this.cancelRecoveryRetry();
    const client = this.rxStomp;
    if (!client) {
      this.recoveringExpiredToken = false;
      return;
    }

    void client.deactivate({ force: true }).then(() => {
      this.auth.refreshToken().subscribe({
        next: () => {
          this.recoveringExpiredToken = false;
          this.recoveryAttempt = 0;
          if (!this.reconnectStopped && this.auth.isAuthenticated()) this.activateClient(client);
        },
        error: () => {
          this.recoveringExpiredToken = false;
          if (!this.reconnectStopped && this.auth.isAuthenticated()) {
            this.scheduleRecoveryRetry(client);
          }
        },
      });
    });
  }

  private scheduleNetworkReconnect(client: RxStomp): void {
    if (
      client !== this.rxStomp ||
      this.recoveringExpiredToken ||
      this.recoveringNetwork ||
      this.reconnectStopped ||
      this.recoveryRetrySubscription ||
      !this.auth.isAuthenticated()
    ) {
      return;
    }
    const delays = [1_000, 2_000, 5_000, 10_000, 30_000];
    const delay = delays[Math.min(this.recoveryAttempt, delays.length - 1)];
    this.recoveryAttempt += 1;
    this.recoveryRetrySubscription = timer(delay).subscribe(() => {
      this.recoveryRetrySubscription = null;
      if (
        client !== this.rxStomp ||
        this.reconnectStopped ||
        this.recoveringExpiredToken ||
        !this.auth.isAuthenticated()
      ) {
        return;
      }
      this.recoveringNetwork = true;
      void client.deactivate({ force: true }).then(() => {
        if (
          client === this.rxStomp &&
          !this.reconnectStopped &&
          !this.recoveringExpiredToken &&
          this.auth.isAuthenticated()
        ) {
          this.activateClient(client);
        } else {
          this.recoveringNetwork = false;
        }
      });
    });
  }

  private scheduleRecoveryRetry(client: RxStomp): void {
    if (client !== this.rxStomp || this.recoveryRetrySubscription) return;
    const delays = [1_000, 2_000, 5_000, 10_000, 30_000];
    const delay = delays[Math.min(this.recoveryAttempt, delays.length - 1)];
    this.recoveryAttempt += 1;
    this.recoveryRetrySubscription = timer(delay).subscribe(() => {
      this.recoveryRetrySubscription = null;
      if (client === this.rxStomp && !this.reconnectStopped && this.auth.isAuthenticated()) {
        this.handleSessionErrorCode('SESSION_TOKEN_EXPIRED', client);
      }
    });
  }

  private cancelRecoveryRetry(): void {
    this.recoveryRetrySubscription?.unsubscribe();
    this.recoveryRetrySubscription = null;
  }
}
